import { CommonModule } from '@angular/common';
import { Component, OnInit } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { ActivatedRoute } from '@angular/router';
import { combineLatest } from 'rxjs';
import { PublicMeetingActionApiService } from '../services/public-meeting-action-api.service';
import { PublicMeetingActionResponse } from '../models/meeting.model';

/**
 * Página PÚBLICA de "cancelar / reprogramar" a la que llega el invitado desde el enlace
 * del correo (`/#/meeting-action/:token`). No hay sesión: el token del enlace es la
 * credencial y todo se resuelve contra `/api/public/meeting-action/**`.
 *
 * INC 79 — antes hablaba con endpoints autenticados (`/api/meeting-action-tokens/lookup`,
 * `GET /api/meetings/{id}` y `PUT /api/meetings/{id}`), así que el visitante recibía 401:
 * la reunión nunca se cargaba, los botones salían por un `return` mudo y, cuando llegaba
 * a enviarse, el PUT —reemplazo total con cuatro campos— borraba lugar, organizador,
 * contacto y zona horaria, y lo rechazaba la validación de modalidad.
 */
@Component({
  selector: 'app-public-meeting-action',
  standalone: true,
  imports: [CommonModule, FormsModule],
  templateUrl: './public-meeting-action.component.html',
})
export class PublicMeetingActionComponent implements OnInit {
  token = '';
  loading = true;
  error = '';

  info: PublicMeetingActionResponse | null = null;

  // Formulario
  reasonText = '';
  newDate = '';
  newTime = '';
  /** INC 79: tope inferior del selector de fecha — nunca antes de hoy. */
  minDate = '';

  submitting = false;
  done = false;
  resultMessage = '';

  constructor(
    private route: ActivatedRoute,
    private api: PublicMeetingActionApiService
  ) {}

  ngOnInit(): void {
    // Se aceptan tanto /meeting-action/:token como ?token=. Se escucha el parámetro
    // (no snapshot): Angular reutiliza el componente al cambiar solo el token y con
    // snapshot se quedaba mostrando el resultado del enlace anterior.
    combineLatest([this.route.paramMap, this.route.queryParamMap]).subscribe(
      ([params, query]) => {
        const token = params.get('token') || query.get('token') || '';
        if (token === this.token) return;
        this.token = token;
        this.reiniciar();
        if (!this.token) {
          this.error = 'Enlace inválido.';
          this.loading = false;
          return;
        }
        this.cargar();
      }
    );
  }

  /** Estado limpio antes de resolver un enlace nuevo. */
  private reiniciar(): void {
    this.info = null;
    this.error = '';
    this.done = false;
    this.resultMessage = '';
    this.reasonText = '';
    this.submitting = false;
    this.loading = true;
  }

  private cargar(): void {
    this.api.resolve(this.token).subscribe({
      next: (info) => {
        this.info = info;
        this.prefillReschedule(info);
        this.loading = false;
      },
      error: (err) => {
        this.error =
          err.error?.message || 'Este enlace no es válido, ya se usó o ha caducado.';
        this.loading = false;
      },
    });
  }

  /**
   * Precarga fecha/hora en la zona horaria del navegador.
   *
   * INC 79: si la reunión ya venció NO se precarga su fecha original —el invitado
   * confirmaría una reprogramación al pasado—, sino la próxima marca de :00 o :30.
   */
  private prefillReschedule(info: PublicMeetingActionResponse): void {
    if (!info.datetimeStart) return;
    const ahora = new Date();
    this.minDate = this.toDateInput(ahora);

    const original = new Date(info.datetimeStart);
    const base =
      isNaN(original.getTime()) || original.getTime() <= ahora.getTime()
        ? this.nextSlot(ahora)
        : original;

    this.newDate = this.toDateInput(base);
    this.newTime = `${String(base.getHours()).padStart(2, '0')}:${String(base.getMinutes()).padStart(2, '0')}`;
  }

  private toDateInput(d: Date): string {
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(
      d.getDate()
    ).padStart(2, '0')}`;
  }

  /** Próxima marca de :00 o :30 estrictamente futura. */
  private nextSlot(from: Date): Date {
    const d = new Date(from.getTime());
    d.setSeconds(0, 0);
    d.setMinutes(d.getMinutes() < 30 ? 30 : 60);
    return d;
  }

  get actionLabel(): string {
    switch (this.info?.actionType) {
      case 'cancel':
        return 'Cancelar reunión';
      case 'reschedule':
        return 'Reprogramar reunión';
      case 'confirm':
        return 'Confirmar asistencia';
      default:
        return 'Gestionar reunión';
    }
  }

  /** "lunes, 8 de septiembre de 2026, 15:00" en la zona horaria de la reunión. */
  formatFecha(iso?: string | null, timezone?: string | null): string {
    if (!iso) return '—';
    try {
      return new Intl.DateTimeFormat('es-ES', {
        weekday: 'long',
        year: 'numeric',
        month: 'long',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
        timeZone: timezone || undefined,
      }).format(new Date(iso));
    } catch {
      return '—';
    }
  }

  get modalidad(): string {
    if (this.info?.location?.trim()) return this.info.location;
    if (this.info?.joinUrl?.trim()) return 'Videollamada';
    return '—';
  }

  // ─────────── Acciones ───────────

  confirmCancel(): void {
    if (!this.info || this.submitting) return;
    this.submitting = true;
    this.error = '';
    this.api
      .cancel(this.token, { reasonText: this.reasonText?.trim() || null })
      .subscribe({
        next: (info) => {
          this.info = info;
          this.submitting = false;
          this.done = true;
          this.resultMessage = 'Tu reunión ha sido cancelada.';
        },
        error: (err) => {
          this.submitting = false;
          this.error = err.error?.message || 'No se pudo cancelar la reunión.';
        },
      });
  }

  confirmReschedule(): void {
    if (!this.info || this.submitting || !this.newDate || !this.newTime) return;

    // INC 79: el `min` del input de fecha no cubre la hora ni el tecleo manual.
    const elegido = this.toLocalDate(this.newDate, this.newTime);
    if (isNaN(elegido.getTime()) || elegido.getTime() <= Date.now()) {
      this.error =
        'La nueva fecha y hora de la reunión debe ser posterior a la fecha y hora actuales.';
      return;
    }

    this.submitting = true;
    this.error = '';
    this.api
      .reschedule(this.token, {
        // Hora de pared del invitado con offset explícito de SU zona (mismo criterio
        // que la página de reserva): el backend recibe el instante exacto que ve.
        datetimeStart: this.toLocalIso(this.newDate, this.newTime),
        reasonText: this.reasonText?.trim() || null,
      })
      .subscribe({
        next: (info) => {
          this.info = info;
          this.submitting = false;
          this.done = true;
          this.resultMessage = 'Tu reunión ha sido reprogramada.';
        },
        error: (err) => {
          this.submitting = false;
          this.error = err.error?.message || 'No se pudo reprogramar la reunión.';
        },
      });
  }

  /** Reintentar tras un error recuperable (plazo, choque de horario…). */
  reintentar(): void {
    this.error = '';
    this.loading = true;
    this.cargar();
  }

  /** Fecha + hora de los dos inputs como Date en la zona horaria del navegador. */
  private toLocalDate(date: string, time: string): Date {
    const [y, m, d] = date.split('-').map(Number);
    const [hh, mm] = time.split(':').map(Number);
    return new Date(y, m - 1, d, hh, mm, 0, 0);
  }

  /** `YYYY-MM-DDTHH:mm:00±HH:mm` con el offset real del navegador para esa fecha. */
  private toLocalIso(date: string, time: string): string {
    const local = this.toLocalDate(date, time);
    const offsetMinutes = -local.getTimezoneOffset();
    const sign = offsetMinutes >= 0 ? '+' : '-';
    const abs = Math.abs(offsetMinutes);
    const oh = String(Math.floor(abs / 60)).padStart(2, '0');
    const om = String(abs % 60).padStart(2, '0');
    const offset = offsetMinutes === 0 ? 'Z' : `${sign}${oh}:${om}`;
    return `${date}T${time}:00${offset}`;
  }
}
